import { type CSSProperties, useEffect, useState } from 'react';

/**
 * A logo/avatar image that can never show a broken or distorted picture: the image sits in a fixed box with
 * object-fit: contain, and missing / unusable / failing URLs fall back to rounded initials on the brand colour.
 */

/** Only URLs that work for everyone: https (or http for local dev) and same-origin paths. Never blob:/data:. */
export function isUsableImageUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  return /^https?:\/\//i.test(url) || (url.startsWith('/') && !url.startsWith('//'));
}

/** "Acme Missions" → "AM", "Missions" → "MI", "" → "M". */
export function initialsOf(name: string | null | undefined): string {
  const w = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!w.length) return 'M';
  const s = w.length > 1 ? `${w[0]![0]}${w[1]![0]}` : w[0]!.slice(0, 2);
  return s.toUpperCase();
}

/** True once the image failed to load (reset when the URL changes). */
function useBroken(src: string | null | undefined): [boolean, () => void] {
  const [broken, setBroken] = useState<string | null>(null);
  useEffect(() => setBroken(null), [src]);
  return [!!src && broken === src, () => setBroken(src ?? null)];
}

export interface BrandLogoProps {
  /** Logo for light backgrounds. */
  src: string | null | undefined;
  /** Logo for dark backgrounds (falls back to `src`). */
  darkSrc?: string | null;
  /** App title / company name → alt text and initials. */
  name: string;
  /** Background of the initials mark (defaults to the brand colour). */
  color?: string;
  /** Show the name next to the initials mark when there is no logo. */
  showName?: boolean;
  /** Box height in px (the logo keeps its aspect ratio inside, max width = 5× height). */
  height?: number;
  className?: string;
}

export function BrandLogo({ src, darkSrc, name, color, showName = false, height = 28, className = '' }: BrandLogoProps) {
  const light = isUsableImageUrl(src) ? src : null;
  const dark = isUsableImageUrl(darkSrc) ? darkSrc : light;
  const [lightBroken, breakLight] = useBroken(light);
  const [darkBroken, breakDark] = useBroken(dark);
  const box = { ['--bl-h' as string]: `${height}px` } as CSSProperties;
  const mark = (
    <span className="bl-ini" style={color ? { background: color } : undefined} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
  if (!light || (lightBroken && (!dark || darkBroken))) {
    return (
      <span className={`brand-logo bl-fallback ${className}`} style={box} role="img" aria-label={name}>
        {mark}
        {showName && <span className="bl-name">{name}</span>}
      </span>
    );
  }
  // both variants only while both load; if one breaks, the other is shown in every theme
  const twoTone = !!dark && dark !== light && !lightBroken && !darkBroken;
  return (
    <span className={`brand-logo ${className}`} style={box}>
      {!lightBroken && <img className={twoTone ? 'brand-light' : undefined} src={light} alt={name} onError={breakLight} />}
      {dark && dark !== light && !darkBroken && (twoTone || lightBroken) && <img className={twoTone ? 'brand-dark' : undefined} src={dark} alt={name} onError={breakDark} />}
    </span>
  );
}

/** Round avatar: photo when it loads, initials on the person's colour otherwise. */
export function AvatarImage({ src, initials, alt = '' }: { src: string | null | undefined; initials: string; alt?: string }) {
  const usable = isUsableImageUrl(src) ? src : null;
  const [broken, onError] = useBroken(usable);
  if (!usable || broken) return <>{initials}</>;
  return <img className="av-img" src={usable} alt={alt} onError={onError} />;
}
